
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import api from "../../utills/api";

const PLATFORMS = ["Universal", "Google", "Meta", "YouTube"];
const CATEGORIES = [
  "IMAGE",
  "LOGO",
  "VIDEO",
  "HEADLINE",
  "LONG_HEADLINE",
  "DESCRIPTION",
];

const MarketingAssetsPage = () => {
  const [assets, setAssets] = useState([]);
  const [platform, setPlatform] = useState("Universal");
  const [category, setCategory] = useState("IMAGE");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  const [form, setForm] = useState({
    name: "",
    platform: "Universal",
    category: "IMAGE",
    file: null,
    description: "",
  });

  const fetchAssets = useCallback(async () => {
    try {
      setLoading(true);

      const response = await api.get("/marketing/assets", {
        params: {
          platform,
          category,
          search: search.trim() || undefined,
        },
      });

      if (!response?.data?.success) {
        throw new Error(
          response?.data?.message || "Unable to fetch marketing assets."
        );
      }

      setAssets(
        response?.data?.data?.assets ||
          response?.data?.data ||
          []
      );
    } catch (err) {
      if (err?.response?.status === 404) {
        setError(
          "Marketing asset-library API is not available yet. Add the backend /marketing/assets endpoints before enabling reusable asset storage."
        );
      } else {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to fetch marketing assets."
        );
      }
    } finally {
      setLoading(false);
    }
  }, [platform, category, search]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const filteredAssets = useMemo(() => {
    const value = search.trim().toLowerCase();

    if (!value) return assets;

    return assets.filter((asset) =>
      String(asset?.name || "").toLowerCase().includes(value)
    );
  }, [assets, search]);

  const createAsset = async (event) => {
    event.preventDefault();

    if (!form.name.trim()) {
      setError("Asset name is required.");
      return;
    }

    if (!form.file) {
      setError("Select a file.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      const body = new FormData();
      body.append("name", form.name.trim());
      body.append("platform", form.platform);
      body.append("category", form.category);
      body.append("description", form.description.trim());
      body.append("file", form.file);

      const response = await api.post("/marketing/assets", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (!response?.data?.success) {
        throw new Error(
          response?.data?.message || "Unable to create asset."
        );
      }

      setSuccess("Marketing asset created successfully.");
      setShowCreate(false);
      setForm({
        name: "",
        platform: "Universal",
        category: "IMAGE",
        file: null,
        description: "",
      });

      await fetchAssets();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to create marketing asset."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Marketing Assets" pageTitle="Marketing" />

        {error && (
          <Alert color="danger" toggle={() => setError("")}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert color="success" toggle={() => setSuccess("")}>
            {success}
          </Alert>
        )}

        <Card>
          <CardBody>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
              <div>
                <h4 className="mb-1">Marketing Asset Library</h4>
                <p className="text-muted mb-0">
                  Upload once, reuse across Google, Meta and YouTube campaigns.
                </p>
              </div>

              <Button color="primary" onClick={() => setShowCreate(true)}>
                <i className="ri-upload-2-line me-1" />
                Upload Asset
              </Button>
            </div>

            <Row className="g-3 mb-4">
              <Col md={4}>
                <Label>Platform</Label>
                <Input
                  type="select"
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                >
                  {PLATFORMS.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col md={4}>
                <Label>Asset Type</Label>
                <Input
                  type="select"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  {CATEGORIES.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col md={4}>
                <Label>Search</Label>
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search assets..."
                />
              </Col>
            </Row>

            {loading ? (
              <div className="text-center py-5">
                <Spinner size="sm" className="me-2" />
                Loading assets...
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover responsive className="align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Asset</th>
                      <th>Platform</th>
                      <th>Type</th>
                      <th>Dimensions</th>
                      <th>Size</th>
                      <th>Status</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredAssets.length ? (
                      filteredAssets.map((asset) => (
                        <tr key={asset._id || asset.id}>
                          <td>
                            <div className="fw-semibold">
                              {asset.name || "-"}
                            </div>
                            <div className="text-muted fs-12">
                              {asset.fileName || asset.url || "-"}
                            </div>
                          </td>
                          <td>
                            <Badge color="light">
                              {asset.platform || "Universal"}
                            </Badge>
                          </td>
                          <td>{asset.category || asset.type || "-"}</td>
                          <td>
                            {asset.width && asset.height
                              ? `${asset.width} × ${asset.height}`
                              : "-"}
                          </td>
                          <td>
                            {asset.size
                              ? `${Math.round(asset.size / 1024)} KB`
                              : "-"}
                          </td>
                          <td>
                            <Badge color="success">
                              {asset.status || "Active"}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center py-5">
                          No assets found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>

        <Modal
          isOpen={showCreate}
          toggle={() => !saving && setShowCreate(false)}
          centered
        >
          <ModalHeader toggle={() => !saving && setShowCreate(false)}>
            Upload Marketing Asset
          </ModalHeader>

          <form onSubmit={createAsset}>
            <ModalBody>
              <div className="mb-3">
                <Label>Asset Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, name: e.target.value }))
                  }
                  placeholder="Admission Landscape 01"
                />
              </div>

              <Row className="g-3">
                <Col md={6}>
                  <Label>Platform</Label>
                  <Input
                    type="select"
                    value={form.platform}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, platform: e.target.value }))
                    }
                  >
                    {PLATFORMS.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </Input>
                </Col>

                <Col md={6}>
                  <Label>Type</Label>
                  <Input
                    type="select"
                    value={form.category}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, category: e.target.value }))
                    }
                  >
                    {CATEGORIES.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </Input>
                </Col>

                <Col md={12}>
                  <Label>File *</Label>
                  <Input
                    type="file"
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        file: e.target.files?.[0] || null,
                      }))
                    }
                  />
                </Col>

                <Col md={12}>
                  <Label>Description</Label>
                  <Input
                    type="textarea"
                    rows="3"
                    value={form.description}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        description: e.target.value,
                      }))
                    }
                  />
                </Col>
              </Row>
            </ModalBody>

            <ModalFooter>
              <Button
                type="button"
                color="light"
                onClick={() => setShowCreate(false)}
                disabled={saving}
              >
                Cancel
              </Button>

              <Button type="submit" color="primary" disabled={saving}>
                {saving && <Spinner size="sm" className="me-2" />}
                Save Asset
              </Button>
            </ModalFooter>
          </form>
        </Modal>
      </Container>
    </div>
  );
};

export default MarketingAssetsPage;
